/**
 * Plain-language words for the "Invite an SI" panel, keyed under `siInvite` in lang/main.json; the English here is the
 * fallback when a language has no translation yet. Written for a non-technical person: what happened and what to do.
 */
export const EN = {
    // TPENGAGE: the SI's "Listening to ..." chip on its tile (kamo-si-listening)
    'siListening.listeningTo': 'Listening to {{names}}',
    'siListening.listeningToMore': 'Listening to {{names}} and {{count}} others',
    'siListening.aria': '{{si}} is listening to {{names}}. Say its name to talk with it.',
    'siListening.someone': 'someone',
    'siInvite.button': 'Invite an SI',
    'siInvite.buttonTooltip': 'Bring one of your SI teammates into this meeting',
    'siInvite.close': 'Close',
    'siInvite.title': 'Invite an SI',
    'siInvite.subtitle': 'Bring a Super Intelligence teammate into {{meeting}}.',
    'siInvite.subtitleNoTitle': 'Bring a Super Intelligence teammate into this meeting.',
    'siInvite.search': 'Search by name or job title',
    'siInvite.groupSupervised': 'You supervise',
    'siInvite.groupSupervisedHint': 'They take direct requests from you.',
    'siInvite.groupAvailable': 'Available to you',
    'siInvite.groupAvailableHint': 'Their own access includes meetings, so anyone on the team can invite them.',
    'siInvite.groupLocked': 'Only their supervisors can invite',
    'siInvite.groupLockedHint': 'Ask someone who supervises them to bring them in.',
    'siInvite.locked.SI_NEEDS_SUPERVISOR': '{{name}}’s own access doesn’t include meetings, so only their supervisors can bring them in.',
    'siInvite.locked.NOT_ALLOWED_TO_INVITE_THIS_SI': '{{name}}’s settings limit who can invite them to meetings.',
    'siInvite.askSupervisor': 'Supervisor only',
    'siInvite.groupPresent': 'Already in this meeting',
    'siInvite.inMeeting': 'In the meeting',
    'siInvite.joining': 'Joining…',
    'siInvite.connecting': 'Connecting to the meeting',
    'siInvite.notNow': 'Not now',
    'siInvite.invite': 'Invite',
    'siInvite.inviteName': 'Invite {{name}}',
    'siInvite.inviting': 'Inviting…',
    'siInvite.joined': 'Joined',
    'siInvite.tryAgain': 'Try again',
    'siInvite.dismiss': 'Dismiss',
    'siInvite.status.AVAILABLE': 'Available',
    'siInvite.status.OFFLINE': 'Not online · can still join',
    'siInvite.status.ON_A_CALL': 'On another call',
    'siInvite.status.PAUSED': 'Paused',
    'siInvite.status.PAUSED_BY_ORG': 'Paused by your organization',
    'siInvite.status.UNAVAILABLE': 'Unavailable right now',
    'siInvite.status.NOT_SET_UP': 'Not set up for calls yet',
    'siInvite.status.UNKNOWN': 'Ready when you are',
    'siInvite.loading': 'Finding your SIs…',
    'siInvite.loadError': 'We couldn’t refresh the list just now. We’ll keep trying.',
    'siInvite.emptyNone': 'There’s no SI you can invite yet',
    'siInvite.emptyNoneHint': 'SIs you supervise show up here, and others once their settings let you invite them.',
    'siInvite.emptyAllHere': 'Everyone you can invite is already here',
    'siInvite.emptySearch': 'No SI matches “{{q}}”',
    'siInvite.footer': 'An SI joins as a participant in a few seconds. Only one copy of each SI can be in a meeting.',
    'siInvite.reason.NOT_ALLOWED_TO_INVITE_THIS_SI': '{{name}}’s settings limit who can invite them. Ask someone who supervises them.',
    'siInvite.reason.SI_NEEDS_SUPERVISOR': '{{name}}’s own access doesn’t include meetings, so only their supervisors can bring them in.',
    'siInvite.reason.MAX_CONCURRENT_CALLS': '{{name}} is busy on another call. Try again when it’s free.',
    'siInvite.reason.MEMBER_NOT_ACTIVE': '{{name}} is paused or switched off right now.',
    'siInvite.reason.NOT_ENROLLED': '{{name}} isn’t set up to join calls yet.',
    'siInvite.reason.NO_DIAL_IN': 'This meeting can’t take SI guests because its phone bridge is off.',
    'siInvite.reason.MEETING_NOT_JOINABLE': 'This meeting has ended, so no one new can join.',
    'siInvite.reason.UNREACHABLE': 'We couldn’t reach {{name}} just now. Try again in a moment.',
    'siInvite.reason.NETWORK': 'You seem to be offline. Check your connection and try again.',
    'siInvite.reason.NOT_IN_MEETING': 'Join the meeting first, then you can invite an SI.',
    'siInvite.reason.NOT_AN_ACTIVE_AI_MEMBER': '{{name}} can’t join meetings right now.',
    'siInvite.reason.DIDNT_JOIN': '{{name}} didn’t make it in. Try again.',
    'siInvite.reason.ALREADY_ATTENDED': '{{name}} was already in this meeting and has left.',
    'siInvite.reason.DEFAULT': '{{name}} couldn’t join. Try again.'
};
const UNREACHABLE = new Set(['RUNTIME_UNAVAILABLE', 'UNAVAILABLE', 'DIAL_FAILED', 'NO_CALL', 'HTTP_502', 'HTTP_503',
    'HTTP_504', 'HTTP_500']);
const KNOWN = new Set(['NOT_ALLOWED_TO_INVITE_THIS_SI', 'SI_NEEDS_SUPERVISOR', 'MAX_CONCURRENT_CALLS', 'MEMBER_NOT_ACTIVE', 'NOT_ENROLLED',
    'NO_DIAL_IN', 'MEETING_NOT_JOINABLE', 'NETWORK', 'NOT_IN_MEETING', 'NOT_AN_ACTIVE_AI_MEMBER', 'DIDNT_JOIN',
    'ALREADY_ATTENDED']);
/**
 * The message key for a refusal code.
 *
 * @param {string} reason - The code.
 * @returns {string}
 */
export function reasonKey(reason) {
    if (!reason) {
        return 'siInvite.reason.DEFAULT';
    }
    if (reason === 'MEETING_NOT_FOUND') {
        return 'siInvite.reason.MEETING_NOT_JOINABLE';
    }
    if (UNREACHABLE.has(reason)) {
        return 'siInvite.reason.UNREACHABLE';
    }
    return KNOWN.has(reason) ? `siInvite.reason.${reason}` : 'siInvite.reason.DEFAULT';
}
/**
 * Fills {{placeholders}}.
 *
 * @param {string} s - The text.
 * @param {Object} vars - The values.
 * @returns {string}
 */
export function fill(s, vars) {
    return s.replace(/{{(\w+)}}/g, (_, k) => (vars?.[k] === undefined ? '' : vars[k]));
}
